import "./WorkflowTemplate.css";

import { ArrowFullLeftDarkIcon, RoundedPlusSignIcon } from "../../../../../assets";
import { OrganisationTypeConstants, WorkflowTemplateOrganisationAccessLevel } from "../../../../../constants";
import { AssetManagerTemplate, DeveloperTemplate, WorkflowTemplatePanel } from "../../../../../templates";
import { useAuth } from "../../../../../useAuth";
import {
  Button,
  Column,
  KanaTooltip,
  ProfileMenu,
  ProfileMenuItem,
  QuickActionsMenu,
  Row,
  UserHeader,
} from "../../../../../widget";
import {
  AddTemplateStepModal,
  ApplyWorkflowTemplatePanel,
  NextPreviousPhaseButtons,
  WorkflowStageSelector,
  WorkflowTemplateSteps,
} from "./components";
import { useWorkflowTemplate } from "./useWorkflowTemplate";

const WorkflowTemplate = (): JSX.Element => {
  const { currentUserType } = useAuth();
  const {
    workflowTemplateUuid,
    workflowTemplate,
    workflowTemplatePhases,
    workflowTemplateOrganisationAccessLevel,
    workflowTemplateOrganisationAccessLevelTooltip,
    workflowTemplateSteps,

    showAddWorkflowTemplateStepPanel,
    setShowAddWorkflowTemplateStepPanel,

    showEditWorkflowTemplatePanel,
    setShowEditWorkflowTemplatePanel,

    showApplyWorkflowTemplatePanel,
    setShowApplyWorkflowTemplatePanel,

    projectPhase,
    handlePhaseChange,

    quickActionsMenuOptionsList,
    showQuickActionsMenu,
    setShowQuickActionsMenu,

    hasAccessLevelAndCanEditTemplate,
    shouldRefreshWorkflowTemplate,

    handleBackClick,
    goBackText,
  } = useWorkflowTemplate();

  const isExpired = workflowTemplate?.validTo ? new Date(workflowTemplate.validTo) < new Date() : false;
  const filteredMenu = !isExpired
    ? quickActionsMenuOptionsList
    : quickActionsMenuOptionsList.filter((item) => item.id === 1);
  const content = (
    <div
      className={`WorkflowTemplateContainer ${
        currentUserType === OrganisationTypeConstants.ASSET_MANAGER ? "AssetManager" : ""
      }`}
    >
      <Row spacingV="ml">
        <Column span={12}>
          {currentUserType === OrganisationTypeConstants.ASSET_MANAGER ? (
            <div className="HeaderContainer">
              <Button
                text={goBackText}
                variant="secondary"
                icon={<ArrowFullLeftDarkIcon />}
                iconPosition="left"
                onClick={handleBackClick}
                hasNoPadding
                minWidth={false}
              />
            </div>
          ) : (
            <UserHeader backButtonText={goBackText} handleBackClick={handleBackClick} />
          )}
        </Column>
      </Row>
      <Row spacingV="ml" justify="between">
        <Column span={12}>
          <div className="WorkflowTemplateEditorRow">
            <h1>{workflowTemplate?.name}</h1>
            {quickActionsMenuOptionsList.length > 0 && (
              <div>
                <QuickActionsMenu onClick={() => setShowQuickActionsMenu(true)} />
                <ProfileMenu
                  show={showQuickActionsMenu}
                  onClose={() => setShowQuickActionsMenu(false)}
                  position={{ top: "0" }}
                >
                  {filteredMenu.map((el) => (
                    <ProfileMenuItem
                      key={el.id}
                      profileMenuItem={el}
                      onClick={() => {
                        if (el.action) {
                          el.action();
                        }
                        setShowQuickActionsMenu(false);
                      }}
                    />
                  ))}
                </ProfileMenu>
              </div>
            )}
          </div>
        </Column>
      </Row>
      <Row spacingV="ml">
        <Column span={12}>
          <p className="body1">{workflowTemplate?.description}</p>
        </Column>
      </Row>
      <Row spacingV="ll">
        <Column span={12}>
          <div className="WorkflowTemplateOrganisationAccessLevelInfo">
            <h3>Your access level: </h3>
            <p className="body1">{workflowTemplateOrganisationAccessLevel}</p>
            <KanaTooltip
              tooltipHeader={workflowTemplateOrganisationAccessLevel ?? WorkflowTemplateOrganisationAccessLevel.Viewer}
              tooltipText={workflowTemplateOrganisationAccessLevelTooltip}
            />
          </div>
        </Column>
      </Row>
      <Row spacingV="ll">
        <Column span={12}>
          <div className="WorkflowTemplateEditorRow">
            <WorkflowStageSelector
              projectPhase={projectPhase}
              phases={workflowTemplatePhases}
              handlePhaseChange={handlePhaseChange}
            />
            {hasAccessLevelAndCanEditTemplate(WorkflowTemplateOrganisationAccessLevel.Contributor) &&
              workflowTemplateSteps &&
              workflowTemplateSteps?.length > 0 && (
                <Button
                  text="Add step"
                  variant="primary"
                  hasNoPadding
                  size="small"
                  icon={<RoundedPlusSignIcon className="RoundedPlusSignIcon" />}
                  onClick={() => setShowAddWorkflowTemplateStepPanel(true)}
                />
              )}
          </div>
        </Column>
      </Row>
      <Row spacingV="ml">
        <Column span={12}>
          <WorkflowTemplateSteps handleAddWorkflowTemplateStep={() => setShowAddWorkflowTemplateStepPanel(true)} />
        </Column>
      </Row>

      <Row spacingV="ll">
        <Column span={12}>
          <NextPreviousPhaseButtons />
        </Column>
      </Row>
      <WorkflowTemplatePanel
        workflowTemplateUuid={workflowTemplateUuid}
        workflowTemplate={workflowTemplate}
        workflowTemplateType={workflowTemplate?.type}
        projectTypesSelectData={undefined} // It is not needed to pass project types data as the project type of a workflow template cannot be changed when editing, and when creating a workflow template, the project type field is only shown if the user selects a workflow template type of "Project", which is not possible in this component as it is only used for existing workflow templates whose type is already defined.
        show={showEditWorkflowTemplatePanel}
        onClose={() => setShowEditWorkflowTemplatePanel(false)}
        setRefresh={shouldRefreshWorkflowTemplate}
      />
      <AddTemplateStepModal
        show={showAddWorkflowTemplateStepPanel}
        closeModal={() => setShowAddWorkflowTemplateStepPanel(false)}
        templateUuid={workflowTemplateUuid}
      />
      <ApplyWorkflowTemplatePanel
        workflowTemplate={workflowTemplate}
        workflowTemplateUuid={workflowTemplateUuid}
        show={showApplyWorkflowTemplatePanel}
        onClose={() => setShowApplyWorkflowTemplatePanel(false)}
      />
    </div>
  );

  if (currentUserType === OrganisationTypeConstants.ASSET_MANAGER) {
    return <AssetManagerTemplate activeElement="Operations">{content}</AssetManagerTemplate>;
  }

  return <DeveloperTemplate activeElement="Workflows">{content}</DeveloperTemplate>;
};

export default WorkflowTemplate;
